import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Room, RoomEvent } from "livekit-client";
import WhiteboardCanvas, { whiteboardLayout } from "./Whiteboard";
import { AdSurface } from "./AdSurface";
import {
  isMultiHead,
  layoutContainerStyle,
  layoutTileStyle,
  layoutSlots,
  layoutHasBoard,
  layoutHasSharedBoard,
} from "./programLayout";
import { attachProgramSlot, type AttachableTrack } from "./programAttach";
import { resolveBoardImageUrl } from "./boardSlates";
import type { BoardSlate, ProgramState } from "./api";
import type { SourceKind } from "./liveConnection";

/** The head slots a layout can compose (a subset of programLayout's LayoutArea). */
type Slot = "a" | "b" | "c" | "d";

// ProgramComposition (LSC-561): the Director-controlled PROGRAM video composition — the
// SAME visual Fan mix — rendered for TALENT (Legend Go-Live + Guest), over their EXISTING
// room connection. Source switching, multi-head layouts (ADR 0054/0212), shared +
// dueling boards with live drawing, slates (pre-start/standby/ended/signal-lost), and the
// FTB blackout — composed exactly as the fan sees. It reuses the SAME source-resolution
// (attachProgramSlot, LSC-177), layout math (programLayout), and overlay sub-components
// (WhiteboardCanvas/AdSurface) as ConsumerWatch, so a slot never resolves differently on
// the talent view vs the fan view.
//
// Deliberately NOT included (talent-specific): AUDIO — talent hear each other on the
// talent-conversation plane (ADR 0218), so this attaches VIDEO ONLY (no echo/duplicate,
// per LSC-561); HLS (talent are always WebRTC publishers in the room); program transitions
// (talent get an instant cut); the pre/post-roll reels + fan chrome. The board DRAWING
// surface is pinned 16:9 (see Whiteboard.tsx), so normalized strokes compose correctly.

export interface ProgramCompositionProps {
  /** The talent's own LiveKit room (publisher connection). */
  room: Room | null;
  /** The current program state (synced on the talent surface + passed in). */
  program: ProgramState;
  /** Admin-uploaded board slates, to resolve a board id → image (ADR 0058). */
  boardSlates: BoardSlate[];
  /** Legend identity for the pre-start / ended slates (optional). */
  legendName?: string;
  legendInitials?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

const stageStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  height: "100%",
  background: "#000",
  overflow: "hidden",
};
const videoStyle: CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000" };
const tileVideoStyle: CSSProperties = { width: "100%", height: "100%", objectFit: "cover", background: "#000" };
const slateStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: 16,
  textAlign: "center",
};
const tileLostStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(10,10,12,0.82)",
  color: "var(--text-dim)",
  fontSize: "var(--fs-2xs)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.04em",
  textAlign: "center",
  padding: 8,
};
const lowerThirdStyle: CSSProperties = {
  position: "absolute",
  left: "4%",
  bottom: "8%",
  maxWidth: "80%",
  padding: "8px 12px",
  background: "rgba(10,10,12,0.72)",
  borderRadius: "var(--radius-sm)",
  color: "var(--text-hi)",
  fontSize: "var(--fs-sm)",
  zIndex: 3,
};
const initialsStyle: CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--surface-3)",
  border: "1px solid var(--gold)",
  color: "var(--gold)",
  fontFamily: "var(--font-display)",
  fontSize: 24,
  fontWeight: 700,
};

export default function ProgramComposition({
  room,
  program,
  boardSlates,
  legendName,
  legendInitials,
  style,
  ariaLabel = "Program monitor",
}: ProgramCompositionProps) {
  const videoRefA = useRef<HTMLVideoElement | null>(null);
  const videoRefB = useRef<HTMLVideoElement | null>(null);
  const videoRefC = useRef<HTMLVideoElement | null>(null);
  const videoRefD = useRef<HTMLVideoElement | null>(null);
  const holderA = useRef<AttachableTrack | null>(null);
  const holderB = useRef<AttachableTrack | null>(null);
  const holderC = useRef<AttachableTrack | null>(null);
  const holderD = useRef<AttachableTrack | null>(null);
  const programRef = useRef<ProgramState>(program);
  programRef.current = program;

  const [kindA, setKindA] = useState<SourceKind>("none");
  const [kindB, setKindB] = useState<SourceKind>("none");
  const [kindC, setKindC] = useState<SourceKind>("none");
  const [kindD, setKindD] = useState<SourceKind>("none");
  // Latch "has been live" on the Director's first TAKE (program.live, ADR 0149) so the
  // pre-start slate holds until the show actually airs — matching the fan. Reset on end.
  const [hasBeenLive, setHasBeenLive] = useState(false);

  useEffect(() => {
    if (program.live) setHasBeenLive(true);
    else if (program.ended) setHasBeenLive(false);
  }, [program.live, program.ended]);

  // Resolve + attach the program source(s) — VIDEO ONLY (audioEl/holder null): talent
  // hear each other via the talent-conversation plane (ADR 0218); attaching program audio
  // here would echo. Mirrors ConsumerWatch.attachSource via the shared attachProgramSlot.
  useEffect(() => {
    const attachAll = (force: boolean) => {
      const prog = programRef.current;
      const slots = layoutSlots(prog.layout);
      const ka = attachProgramSlot(room, prog.source, videoRefA.current, null, holderA, null, force);
      const kb = slots.includes("b") ? attachProgramSlot(room, prog.sourceB, videoRefB.current, null, holderB, null, force) : "none";
      const kc = slots.includes("c") ? attachProgramSlot(room, prog.sourceC, videoRefC.current, null, holderC, null, force) : "none";
      const kd = slots.includes("d") ? attachProgramSlot(room, prog.sourceD, videoRefD.current, null, holderD, null, force) : "none";
      setKindA(ka);
      setKindB(kb);
      setKindC(kc);
      setKindD(kd);
    };
    attachAll(true); // program/layout changed — force a re-attach onto the (re)mounted tiles
    if (!room) return;
    const reattach = () => attachAll(false);
    const forceReattach = () => attachAll(true);
    room.on(RoomEvent.TrackSubscribed, reattach);
    room.on(RoomEvent.TrackUnsubscribed, reattach);
    room.on(RoomEvent.TrackPublished, reattach);
    room.on(RoomEvent.TrackSubscriptionFailed, reattach);
    room.on(RoomEvent.ParticipantConnected, reattach);
    room.on(RoomEvent.ParticipantDisconnected, reattach);
    room.on(RoomEvent.Reconnected, forceReattach); // reconnect may re-mint tracks (ADR 0042)
    return () => {
      room.off(RoomEvent.TrackSubscribed, reattach);
      room.off(RoomEvent.TrackUnsubscribed, reattach);
      room.off(RoomEvent.TrackPublished, reattach);
      room.off(RoomEvent.TrackSubscriptionFailed, reattach);
      room.off(RoomEvent.ParticipantConnected, reattach);
      room.off(RoomEvent.ParticipantDisconnected, reattach);
      room.off(RoomEvent.Reconnected, forceReattach);
    };
    // Re-run when the composition's sources/layout change (new elements to bind) or the room swaps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, program.source, program.sourceB, program.sourceC, program.sourceD, program.layout]);

  const multiHead = isMultiHead(program.layout);
  const activeSlots = layoutSlots(program.layout);
  const overlay = program.overlay;
  // The whiteboard as a composited SOURCE for single/dual (NOT the board-layouts, which
  // render the board as a dedicated region below) — mirrors ConsumerWatch.
  const wb = overlay?.kind === "whiteboard" && !layoutHasBoard(program.layout) ? overlay : null;
  const lay = wb ? whiteboardLayout(wb.layout) : null;
  const sharedBoard = overlay?.kind === "whiteboard" ? overlay : null;
  const kindFor = (s: Slot): SourceKind => (s === "a" ? kindA : s === "b" ? kindB : s === "c" ? kindC : kindD);
  const srcFor = (s: Slot): string | undefined =>
    s === "a" ? program.source : s === "b" ? program.sourceB : s === "c" ? program.sourceC : program.sourceD;
  const refFor = (s: Slot) => (s === "a" ? videoRefA : s === "b" ? videoRefB : s === "c" ? videoRefC : videoRefD);
  const slotLost = (s: Slot) => !!srcFor(s) && kindFor(s) === "none";

  // Slate gating — mirror the fan precedence (ended > standby > FTB > ad/hold > signal-lost
  // > audio-led > live). Pre-start holds until the first TAKE (hasBeenLive), and (like the
  // fan) is suppressed while a whiteboard is staged as a source on single/dual.
  const showPreStart = !hasBeenLive && !wb && !program.standby && !program.ended && !program.videoPaused;
  const singleLost = !multiHead && hasBeenLive && !!program.source && kindA === "none" && !program.standby && !program.ended && overlay?.kind !== "ad";
  const blackoutOn = !!program.blackout && !program.ended;

  const identity = (
    <>
      {legendInitials && <div aria-hidden style={initialsStyle}>{legendInitials}</div>}
      <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(20px, 3.2vw, 32px)", fontWeight: 700, color: "var(--text-hi)", textTransform: "uppercase" }}>
        {legendName ?? "The CaveCast"}
      </div>
    </>
  );

  return (
    <div style={{ ...stageStyle, ...style }} aria-label={ariaLabel}>
      {multiHead && program.layout ? (
        <div style={layoutContainerStyle(program.layout)} aria-label={`Program layout ${program.layout}`}>
          {activeSlots.map((s) => (
            <div key={s} style={layoutTileStyle(s)}>
              <video ref={refFor(s)} autoPlay playsInline muted aria-label={`Program video slot ${s.toUpperCase()}`} style={tileVideoStyle} />
              {slotLost(s) && <div style={tileLostStyle} aria-label={`Slot ${s.toUpperCase()} signal lost`}>Signal lost — reconnecting…</div>}
            </div>
          ))}
          {layoutHasSharedBoard(program.layout) && (
            <div style={layoutTileStyle("board")}>
              <WhiteboardCanvas
                strokes={sharedBoard?.strokes ?? []}
                board={sharedBoard?.board}
                boardImageUrl={resolveBoardImageUrl(sharedBoard?.board, boardSlates)}
                ariaLabel="Shared whiteboard"
                style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }}
              />
            </div>
          )}
          {program.layout === "dueling" && (
            <>
              <div style={layoutTileStyle("boardA")}>
                <WhiteboardCanvas strokes={program.boards?.a?.strokes ?? []} board={program.boards?.a?.board} boardImageUrl={resolveBoardImageUrl(program.boards?.a?.board, boardSlates)} ariaLabel="Slot A board" style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
              </div>
              <div style={layoutTileStyle("boardB")}>
                <WhiteboardCanvas strokes={program.boards?.b?.strokes ?? []} board={program.boards?.b?.board} boardImageUrl={resolveBoardImageUrl(program.boards?.b?.board, boardSlates)} ariaLabel="Slot B board" style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
              </div>
            </>
          )}
        </div>
      ) : (
        <video ref={videoRefA} autoPlay playsInline muted aria-label="Program video" style={lay ? { ...videoStyle, ...lay.video } : videoStyle} />
      )}

      {/* Whiteboard-as-source (single/dual), positioned per its layout (full/pip/side). */}
      {wb && lay && (
        <div style={lay.board}>
          <WhiteboardCanvas strokes={wb.strokes ?? []} board={wb.board} boardImageUrl={resolveBoardImageUrl(wb.board, boardSlates)} ariaLabel="Whiteboard on program" style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
          {wb.text && <div style={lowerThirdStyle}>{wb.text}</div>}
        </div>
      )}
      {/* An ad break replaces the feed full-frame (muted — the talent monitor is video-only). */}
      {overlay?.kind === "ad" && overlay.adId && <AdSurface overlay={overlay} muted />}
      {/* Lower third (ADR 0025/0088). */}
      {overlay?.kind === "lower-third" && (overlay.title || overlay.subtitle) && (
        <div style={{ ...lowerThirdStyle, display: "flex", flexDirection: "column", gap: 3, alignItems: overlay.position === "center" ? "center" : "flex-start", textAlign: overlay.position === "center" ? "center" : "left" }}>
          <div style={{ width: 34, height: 3, borderRadius: 2, background: overlay.accent || "var(--gold)" }} />
          <div>
            <strong>{overlay.title}</strong>
            {overlay.subtitle ? <span style={{ opacity: 0.85, fontWeight: 400 }}> · {overlay.subtitle}</span> : null}
          </div>
        </div>
      )}

      {/* Pre-start — the show hasn't been TAKEn live yet (what fans see). */}
      {showPreStart && (
        <div style={{ ...slateStyle, background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)" }} aria-label="Starting soon">
          {identity}
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "var(--fs-2xs)", letterSpacing: "0.04em", color: "var(--gold)" }}>Not live to fans yet</div>
        </div>
      )}
      {/* Production hold — video paused for fans (ADR 0055). */}
      {program.videoPaused && !program.standby && !program.ended && (
        <div style={{ ...slateStyle, background: "rgba(8,8,10,0.9)" }} aria-label="Paused">
          <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(18px,3vw,30px)", fontWeight: 700, color: "var(--text-hi)" }}>Paused</div>
          <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>Fans see a hold slate.</div>
        </div>
      )}
      {/* Standby (recoverable pause, ADR 0039). */}
      {program.standby && !program.ended && (
        <div style={{ ...slateStyle, background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)" }} aria-label="Standby">
          <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(22px, 3.6vw, 38px)", fontWeight: 700, color: "var(--text-hi)" }}>Standby</div>
          <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>The broadcast is paused.</div>
        </div>
      )}
      {/* Involuntary single-source loss (ADR 0042). */}
      {singleLost && (
        <div style={{ ...slateStyle, background: "radial-gradient(120% 120% at 50% 0%, #241f1a 0%, #0c0c0f 70%)" }} aria-label="Signal lost">
          <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(20px, 3.4vw, 34px)", fontWeight: 700, color: "var(--text-hi)" }}>Signal lost — reconnecting…</div>
        </div>
      )}
      {/* Fade to black (ADR 0082). */}
      <div aria-hidden={!blackoutOn} style={{ position: "absolute", inset: 0, zIndex: 4, background: "#000", opacity: blackoutOn ? 1 : 0, pointerEvents: blackoutOn ? "auto" : "none", transition: "opacity 260ms ease" }} />
      {/* Ended (ADR 0104) — endedBoard image, else a text wrap card (no post-roll reel on the monitor). */}
      {program.ended &&
        (program.endedBoard ? (
          <div style={{ ...slateStyle, padding: 0, background: "#000" }} aria-label="Broadcast ended">
            <img src={program.endedBoard} alt="Broadcast ended" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
        ) : (
          <div style={{ ...slateStyle, background: "radial-gradient(120% 120% at 50% 0%, #1f2024 0%, #0a0a0c 70%)" }} aria-label="Broadcast ended">
            <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(22px, 3.6vw, 38px)", fontWeight: 700, color: "var(--text-hi)" }}>That's a wrap</div>
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>This broadcast has ended.</div>
          </div>
        ))}
    </div>
  );
}
